<template>
  <div class="w-full max-w-4xl mx-auto space-y-6 px-3 sm:px-0">
    <!-- Hero Section -->
    <div class="text-center space-y-3">

      <h1 class="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white leading-tight">
        {{ $t('AI_ONBOARDING.DEMO_HOOK.TITLE') || 'Chúc mừng! Bạn đã có AI Chatbot!' }}
      </h1>
      
      <p class="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed">
        {{ $t('AI_ONBOARDING.DEMO_HOOK.SUBTITLE') || 'Thử chat ngay để xem nó hoạt động như thế nào' }}
      </p>
    </div>

    <!-- Demo Chat Box -->
    <div class="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <!-- Chat Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 sm:px-6 py-4 flex items-center gap-3">
        <div class="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
          <lucide-icon name="bot" :size="24" class="text-white" />
        </div>
        <div class="flex-1">
          <h3 class="text-white font-semibold">{{ $t('AI_ONBOARDING.DEMO_HOOK.BOT_NAME') || 'Demo AI Bot' }}</h3>
          <p class="text-white/80 text-xs">{{ $t('AI_ONBOARDING.DEMO_HOOK.BOT_STATUS') || 'Đang online' }}</p>
        </div>
        <span class="px-3 py-1 bg-white/20 rounded-full text-white text-xs font-medium">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.DEMO_MODE') || 'DEMO' }}
        </span>
      </div>

      <!-- Chat Messages -->
      <div class="relative p-4 sm:p-6 space-y-4 h-[360px] sm:h-[420px] md:h-[480px] max-h-[65vh] overflow-y-auto bg-slate-50 dark:bg-slate-900">
        <div v-for="(msg, idx) in messages" :key="idx" class="flex items-start gap-3" :class="msg.role === 'user' ? 'flex-row-reverse ml-auto' : ''">
          <div v-if="msg.role === 'bot'" class="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shrink-0">
            <lucide-icon name="bot" :size="16" class="text-white" />
          </div>
          
          <div v-if="msg.role === 'user'" class="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center shrink-0">
            <lucide-icon name="user" :size="16" class="text-slate-600 dark:text-slate-300" />
          </div>
          
          <div class="max-w-[80%] sm:max-w-[70%] px-4 py-3 rounded-2xl text-sm leading-relaxed" :class="msg.role === 'user' ? 'bg-n-vars-right-bubble text-white' : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-700'">
            <p class="text-sm" v-html="msg.content"></p>
          </div>
        </div>

        <div v-if="isTyping" class="flex items-start gap-3">
          <div class="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
            <lucide-icon name="bot" :size="16" class="text-white" />
          </div>
          <div class="px-4 py-3 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <div class="flex gap-1">
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
          </div>
        </div>

        <!-- Quick Suggestions - Badge style, bottom right -->
        <div v-if="messages.length === 1 && !isTyping" class="absolute bottom-4 right-4 flex flex-wrap gap-2 justify-end">
          <button
            v-for="suggestion in suggestions"
            :key="suggestion"
            class="px-2.5 py-1 text-xs font-medium bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-full hover:bg-slate-50 dark:hover:bg-slate-700 hover:border-hilab dark:hover:border-hilab-dark hover:text-hilab dark:hover:text-hilab-dark transition-all shadow-sm"
            @click="selectSuggestion(suggestion)"
            :disabled="isTyping"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

      <!-- Chat Input -->
      <div class="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
        <div class="flex gap-2">
          <input
            v-model="userInput"
            type="text"
            :placeholder="$t('AI_ONBOARDING.DEMO_HOOK.INPUT_PLACEHOLDER') || 'Nhập câu hỏi của bạn...'"
            class="flex-1 px-4 py-3 rounded-xl bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-sm focus:bg-slate-50 dark:focus:bg-slate-900 focus:outline-none text-sm"
            @keyup.enter="sendMessage"
            :disabled="isTyping"
          />
          <woot-button
            color-scheme="primary"
            :disabled="!userInput.trim() || isTyping"
            @click="sendMessage"
            class="!bg-hilab hover:!bg-hilab-dark dark:!bg-hilab-dark dark:hover:!bg-hilab"
          >
            <lucide-icon name="send" :size="18" />
          </woot-button>
        </div>
      </div>
    </div>

    <!-- Warning Box -->
    <div v-if="showLimitWarning" class="flex items-start gap-3 p-3 bg-orange-50/50 dark:bg-orange-900/10 border-l-4 border-orange-500 dark:border-orange-400 rounded">
      <lucide-icon name="alert-triangle" :size="18" class="text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
      <div class="flex-1">
        <p class="text-sm font-medium text-orange-900 dark:text-orange-100">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.LIMIT_WARNING_TITLE') || 'Sắp hết tin nhắn demo' }}
        </p>
        <p class="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.LIMIT_WARNING_TEXT') || 'Bạn chỉ còn vài tin nhắn demo. Tiếp tục setup để chat không giới hạn!' }}
        </p>
      </div>
    </div>
    <div v-else class="flex items-start gap-3 p-3 bg-yellow-50/50 dark:bg-yellow-900/10 border-l-4 border-yellow-500 dark:border-yellow-400 rounded">
      <lucide-icon name="info" :size="18" class="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
      <div class="flex-1">
        <p class="text-sm font-medium text-yellow-900 dark:text-yellow-100">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.WARNING_TITLE') || 'Demo với AI thật' }}
        </p>
        <p class="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.WARNING_TEXT') || 'Bạn có 5 tin nhắn demo miễn phí. Setup ngay để chat không giới hạn!' }}
        </p>
      </div>
    </div>

    <!-- Stats -->
    <div class="flex flex-wrap items-center justify-center gap-6 sm:gap-8 py-4 text-center">
      <div>
        <p class="text-xl font-bold text-slate-900 dark:text-white">80%</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.STAT_COMPLETION') || 'Users hoàn thành setup' }}
        </p>
      </div>
      <div class="w-px h-8 bg-slate-200 dark:bg-slate-700"></div>
      <div>
        <p class="text-xl font-bold text-slate-900 dark:text-white">5-7</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.STAT_TIME') || 'Phút để setup' }}
        </p>
      </div>
      <div class="w-px h-8 bg-slate-200 dark:bg-slate-700"></div>
      <div>
        <p class="text-xl font-bold text-slate-900 dark:text-white">24/7</p>
        <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
          {{ $t('AI_ONBOARDING.DEMO_HOOK.STAT_UPTIME') || 'Bot hoạt động' }}
        </p>
      </div>
    </div>

  </div>
</template>

<script>
import LucideIcon from 'dashboard/components/LucideIcon.vue';

export default {
  name: 'AiDemoHookStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'skip'],

  data() {
    return {
      userInput: '',
      isTyping: false,
      showLimitWarning: false,
      messages: [
        {
          role: 'bot',
          content: this.$t('AI_ONBOARDING.DEMO_HOOK.INITIAL_MESSAGE') || 'Xin chào! Mình là AI Bot. Mình có thể giúp gì cho bạn? 😊',
        },
      ],
      suggestions: [
        this.$t('AI_ONBOARDING.DEMO_HOOK.SUGGESTION_1') || 'Giá sản phẩm?',
        this.$t('AI_ONBOARDING.DEMO_HOOK.SUGGESTION_2') || 'Giao hàng mất bao lâu?',
        this.$t('AI_ONBOARDING.DEMO_HOOK.SUGGESTION_3') || 'Có ưu đãi không?',
      ],
    };
  },

  methods: {
    async sendMessage() {
      if (!this.userInput.trim() || this.isTyping) return;

      // Add user message
      this.messages.push({
        role: 'user',
        content: this.userInput,
      });

      const userMessage = this.userInput;
      this.userInput = '';
      this.isTyping = true;
      this.scrollToBottom();

      try {
        // Gọi AI thật
        const response = await this.$store.dispatch('aiOnboarding/demoChat', {
          message: userMessage,
          provider_setup: this.$store.state.aiOnboarding.providerSetup || { credentialMode: 'system' },
          bot_profile: this.$store.state.aiOnboarding.botProfile || {},
          bot_modes: this.$store.state.aiOnboarding.botModes || {},
        });

        if (response.data && response.data.success) {
          this.messages.push({
            role: 'bot',
            content: response.data.message,
          });

          // Hiển thị số tin nhắn còn lại
          if (response.data.remaining !== undefined && response.data.remaining <= 2) {
            this.showLimitWarning = true;
          }
        } else {
          throw new Error(response.data?.error || 'Không thể kết nối với AI');
        }
      } catch (error) {
        console.error('Demo chat failed:', error);
        this.messages.push({
          role: 'bot',
          content: error.response?.data?.error || 'Xin lỗi, có lỗi xảy ra. Vui lòng thử lại sau.',
        });
      } finally {
        this.isTyping = false;
        this.scrollToBottom();
      }
    },

    selectSuggestion(suggestion) {
      this.userInput = suggestion;
      this.sendMessage();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const chatBox = this.$el.querySelector('.overflow-y-auto');
        if (chatBox) {
          chatBox.scrollTop = chatBox.scrollHeight;
        }
      });
    },
  },
};
</script>
